import React, { useState, useCallback } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import useAxios from 'hooks/useAxios';
import ImageUploader from 'components/common/ImageUploader';
import { addImage, removeImage } from 'reducers/createListingReducer';
import { DELETE_FILE } from 'graphql/file';

const buildUploadForm = file => {
  const formData = new FormData();
  formData.append(
    'operations',
    JSON.stringify({
      query: `mutation($file: Upload!!) { 
        uploadFile(file: $file) {
          id
        }
      }`,
      variables: {
        file: null
      }
    })
  );
  formData.append(
    'map',
    JSON.stringify({
      file: ['variables.file']
    })
  );
  formData.append('0', file);
  return formData;
};

const ListingDetailsForm = ({ register, errors }) => {
  const { uploadedImages } = useSelector(state => state.createListing);
  const { graphQLURI } = useSelector(state => state.config);
  const [deleteFileMutation] = useMutation(DELETE_FILE);

  const [progress, setProgress] = useState(0);
  const dispatch = useDispatch();
  const axios = useAxios();

  const uploadFiles = useCallback(
    async files => {
      try {
        for (const file of files) {
          if (
            uploadedImages.find(
              f => f.path === file.path && f.size === file.size
            )
          ) {
            continue;
          }
          const formData = buildUploadForm(file);
          const response = await axios.post(graphQLURI, formData, {
            onUploadProgress: progressEvent => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setProgress(percentCompleted);
            }
          });
          dispatch(
            addImage({
              id: response.data.data.uploadFile.id,
              path: file.path,
              size: file.size,
              preview: URL.createObjectURL(file)
            })
          );
        }
      } catch (error) {
        console.error('Error uploading files: ', error);
      }
    },
    [uploadedImages]
  );

  const handleFilesAdded = useCallback(
    async files => {
      await uploadFiles(files);
    },
    [uploadFiles]
  );

  const handleFileRemoved = file => {
    deleteFileMutation({
      variables: {
        input: {
          id: file.id
        }
      }
    });
    dispatch(removeImage(file.id));
  };

  return (
    <>
      <Row className="mb-3 g-3">
        <Col lg={6}>
          <Form.Group as={Col} lg={12}>
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              {...register('title', { required: 'Title is required.' })}
              isInvalid={errors.title}
            />
            <Form.Control.Feedback type="invalid">
              {errors.title?.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} lg={12}>
            <Form.Label>Introduction</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              {...register('introduction', {
                required: 'Introduction is required.'
              })}
              isInvalid={errors.introduction}
            />
            <Form.Control.Feedback type="invalid">
              {errors.introduction?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Form.Group as={Col} lg={6}>
          <Form.Label>Upload images</Form.Label>
          <ImageUploader
            onFilesAdded={handleFilesAdded}
            onFileRemoved={handleFileRemoved}
            files={uploadedImages}
            progress={progress}
          />
        </Form.Group>
      </Row>
    </>
  );
};

ListingDetailsForm.propTypes = {
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired
};

export default ListingDetailsForm;
